import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MessageService } from "primeng/api";
import { FeedbackApi } from "../../api/feedback.api";

@Component({
  selector: "app-abi-nps-survey-dialog",
  templateUrl: "./abi-nps-survey-dialog.component.html",
  styleUrls: ["./abi-nps-survey-dialog.component.scss"],
  standalone: false,
})
export class AbiNpsSurveyDialogComponent implements OnInit {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  readonly ratingScale = Array.from({ length: 11 }, (_, i) => i);
  readonly scoreInfluences = ["Product", "Process", "People"];
  readonly surveyForm: FormGroup;
  showSurvey = false;
  isSubmitting = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly messageService: MessageService,
    private readonly feedbackApi: FeedbackApi,
  ) {
    this.surveyForm = this.fb.group({
      rating: [null, Validators.required],
      influences: [[] as string[], Validators.required],
      workedWell: [""],
      canImprove: [""],
    });
  }

  ratingScaleAnchor(rating: number): string {
    if (rating <= 6) return "Detractor";
    if (rating <= 8) return "Passive";
    return "Promoter";
  }

  toggleInfluence(influence: string, checked: boolean): void {
    const selected = this.surveyForm.get("influences")?.value as string[];
    this.surveyForm
      .get("influences")
      ?.setValue(
        checked
          ? [...selected, influence]
          : selected.filter((item) => item !== influence),
      );
  }

  ngOnInit() {
    this.feedbackApi.hasSubmitted(this.getCurrentPeriod()).subscribe({
      next: (submitted) => {
        this.showSurvey = submitted === false;
        if (submitted) {
          this.close();
        }
      },
      error: () => {
        // Only show the survey when the API explicitly confirms it is available.
        this.showSurvey = false;
        this.close();
      },
    });
  }

  private getCurrentPeriod(): string {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    return `${now.getFullYear()}-Q${quarter}`;
  }

  close() {
    this.visible = false;
    this.showSurvey = false;
    this.visibleChange.emit(false);
  }

  submit() {
    if (this.surveyForm.invalid) {
      this.surveyForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formValue = this.surveyForm.getRawValue();
    this.feedbackApi
      .submitFeedback(this.getCurrentPeriod(), [
        {
          question1: formValue.rating,
          question2: formValue.influences,
          question3: formValue.workedWell,
          question4: formValue.canImprove,
        },
      ])
      .subscribe({
        next: () => {
          this.close();
          this.surveyForm.reset({
            rating: null,
            influences: [],
            workedWell: "",
            canImprove: "",
          });
          this.isSubmitting = false;
          this.messageService.add({
            key: "abi-nps-survey",
            severity: "success",
            summary: "Thank you",
            detail: "Thank you for sharing your feedback.",
          });
        },
        error: () => {
          this.isSubmitting = false;
          this.messageService.add({
            key: "abi-nps-survey",
            severity: "error",
            summary: "Unable to submit",
            detail: "We could not submit your feedback. Please try again.",
          });
        },
      });
  }
}
